'use client';

import React, { createContext, useContext, ReactNode } from 'react';
import { useAccessibility, UseAccessibilityOptions } from './hooks/useAccessibility';

interface AccessibilityContextType {
  announce: (message: string, priority?: 'polite' | 'assertive') => void;
  saveFocus: () => void;
  restoreFocus: () => void;
  focusElement: (element: HTMLElement | string) => void;
  handleKeyboardNavigation: (
    event: React.KeyboardEvent,
    callbacks: {
      onEnter?: () => void;
      onSpace?: () => void;
      onArrowUp?: () => void;
      onArrowDown?: () => void;
      onArrowLeft?: () => void;
      onArrowRight?: () => void;
      onEscape?: () => void;
      onTab?: () => void;
    }
  ) => void;
  announceLoading: (isLoading: boolean, loadingMessage?: string, completedMessage?: string) => void;
  announcePageChange: (pageName: string) => void;
}

const AccessibilityContext = createContext<AccessibilityContextType | null>(null);

interface AccessibilityProviderProps {
  children: ReactNode;
  options?: UseAccessibilityOptions;
}

export function AccessibilityProvider({ children, options = {} }: AccessibilityProviderProps) {
  const accessibility = useAccessibility({
    announcePageChanges: true,
    manageFocus: true,
    announceLoadingStates: true,
    ...options,
  });

  return (
    <AccessibilityContext.Provider value={accessibility}>
      {children}
    </AccessibilityContext.Provider>
  );
}

export function useAccessibilityContext() {
  const context = useContext(AccessibilityContext);
  if (!context) {
    throw new Error('useAccessibilityContext must be used within an AccessibilityProvider');
  }
  return context;
}

export default AccessibilityProvider;